import Clarity from "@microsoft/clarity";
import LogRocket from "logrocket";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import "./App.css";
import { RotateIcon, UpdateReloadIcon } from "./Assets/assets";
import Button from "./Components/Button/Button";
import Header from "./Components/Header/Header";
import SnackBar from "./Components/SnackBar/SnackBar";
import { useComplaints } from "./CustomHooks/useComplaint";
import useLiveLocation from "./CustomHooks/useLiveLocation";
import { useNetworkStatus } from "./CustomHooks/useNetworkStatus";
import { useServiceWorkerMessageSync } from "./CustomHooks/useServiceWorkerMessageSync";
import {
  forceRefreshAccessToken,
  forceRefreshAccessTokenAndRetryAPI,
  logout
} from "./Redux/authentication/actions";
import { setOfflineCaptures } from "./Redux/faceComparison/action";
import { addUserDistance } from "./Redux/locations/action";
import {
  setOfflineProcedureHistory,
  setSelectedProcedure
} from "./Redux/procedures/action";
import Complaints from "./Screens/Complaints/Complaints";
import CreateComplaint from "./Screens/CreateComplaint/CreateComplaint";
import FaceIdentification from "./Screens/FaceIdentification/FaceIdentification";
import Home from "./Screens/Home/Home";
import LocationSearch from "./Screens/LocationSearch/LocationSearch";
import Login from "./Screens/Login/Login";
import Procedures from "./Screens/Procedures/Procedures";
import Profile from "./Screens/Profile/Profile";
import SelectedComplaint from "./Screens/SelectedComplaint/SelectedComplaint";
import SelectedProcedure from "./Screens/SelectedProcedure/SelectedProcedure";
import ViewUpdates from "./Screens/ViewUpdates/ViewUpdates";
import { useAuthSubscriber } from "./Services/authentication";
import {
  PROFILE_SYNC_TIMEOUT_MS,
  useCapturesListener,
  useProfileListener
} from "./Services/database";
import { isValidObject } from "./Services/validators";
import * as Sentry from "@sentry/react";
import { isLocalHost } from "./Utils/constants";
// import UnsupportedDevice from "./Components/UnsupportedDevice/UnsupportedDevice";

function App(props) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [auth, setAuth] = useState({
    loggedIn: null,
    claims: null
  });
  const [locationAccess, setLocationAccess] = useState(null);
  const [cameraAccess, setCameraAccess] = useState(null);
  const isOnline = useNetworkStatus();

  useLiveLocation({
    isAuth: auth.loggedIn,
    isLocationPermissionGranted: locationAccess === "granted"
  });

  useEffect(() => {
    if (props.locations.selectedLocation) {
      addUserDistance();
    }
    // eslint-disable-next-line
  }, [props.locations?.currentLocation, props.locations.selectedLocation]);

  useAuthSubscriber((props) => {
    console.log("CLAIMS:", props.claims);
    setAuth({ claims: props.claims, loggedIn: props.isAuth });
  }, auth.loggedIn);

  const profileSyncState = useProfileListener({
    isAuth: auth.loggedIn,
    phoneNumber: props.auth.data.phoneNumber,
    accessToken: props.auth.data.accessToken
  });

  useCapturesListener({
    isAuth: auth.loggedIn && auth.claims?.surveillance?.read,
    phoneNumber: props.auth.data.phoneNumber
  });

  useServiceWorkerMessageSync(
    setOfflineCaptures,
    setOfflineProcedureHistory,
    forceRefreshAccessTokenAndRetryAPI
  );

  const [getComplaintsNextPage, complaintsLoading] = useComplaints({
    filterData: props.complaints?.filter,
    isAuth: auth.loggedIn && auth.claims?.complaints?.read?.connected,
    locationData: props.locations.data,
    phoneNumber: props.auth?.data?.phoneNumber,
    searchKey: props.complaints?.searchKey,
    sortBy: props.complaints?.sortBy,
    navigate: navigate
  });

  useEffect(() => {
    if (props.profile?.data?.accessSyncStatus === false && auth.loggedIn) {
      props.forceRefreshAccessToken(true);
    }
    if (props.profile?.data && auth.loggedIn && !isLocalHost()) {
      Sentry.setUser({
        id: props.profile.data.employeeId || null,
        name: props.profile.data.name || null,
        phone: props.profile.data.phoneNumber || null
      });

      Sentry.logger.debug(
        `Login successfully - ${props.profile.data?.phoneNumber || null}`
      );
    }
    // eslint-disable-next-line
  }, [props.profile?.data, auth.loggedIn]);

  //for snack bar messages
  useEffect(() => {
    if (props.status.message) {
      setStatus(props.status.message);
    } else {
      setStatus(null);
    }
  }, [props.status]);

  //for log rocket init
  useEffect(() => {
    if (
      process.env.NODE_ENV === "production" &&
      window.location.hostname !== "localhost"
    ) {
      const release =
        typeof process.env.REACT_APP_BUILD_NUM === "string"
          ? { release: process.env.REACT_APP_BUILD_NUM }
          : {};
      if (process.env.REACT_APP_LOG_ROCKET_KEY) {
        LogRocket.init(process.env.REACT_APP_LOG_ROCKET_KEY, release);
      }

      if (process.env.REACT_APP_CLARITY_KEY) {
        const projectId = process.env.REACT_APP_CLARITY_KEY;
        Clarity.init(projectId);
        Clarity.identify(props.auth.data.uid);
      }
    }
  }, [props.auth.data.uid]);

  const handlePermissionChange = (setAccess) => (event) => {
    setAccess(event.target.state);
  };

  const queryPermission = (name, setAccess) => {
    if (navigator.permissions) {
      navigator.permissions.query({ name }).then((result) => {
        setAccess(result.state);
        const handleChange = handlePermissionChange(setAccess);
        result.addEventListener("change", handleChange);

        return () => {
          result.removeEventListener("change", handleChange);
        };
      });
    }
  };

  useEffect(() => {
    const cleanupCamera = queryPermission("camera", setCameraAccess);
    const cleanupLocation = queryPermission("geolocation", setLocationAccess);

    return () => {
      if (cleanupCamera) {
        cleanupCamera();
      }
      if (cleanupLocation) {
        cleanupLocation();
      }
    };
    // eslint-disable-next-line
  }, []);

  const requestLocationPermission = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationAccess("granted");
      },
      (error) => {
        console.error("Location permission denied or failed", error);
        setLocationAccess("denied");
      }
    );
  };

  const requestCameraPermission = () => {
    // wil not work on local host
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        setCameraAccess("granted");
      })
      .catch((err) => {
        console.error("Camera permission denied or failed", err);
        setCameraAccess("denied");
      });
  };

  useEffect(() => {
    const { theme } = props.auth;
    Object.keys(theme).forEach((key) => {
      document.documentElement.style.setProperty(`--${key}`, theme[key]);
      document.documentElement.setAttribute(
        "data-theme",
        props.auth.theme.primaryColor === "#0E0E0E" ? "dark" : "light"
      );
    });

    // eslint-disable-next-line
  }, [props.auth.theme]);

  // First-ever load with nothing persisted yet (fresh device, cleared site
  // data, incognito): profile.data is null and the listener hasn't produced
  // a definitive answer. A user who already has profile data - the common
  // case, since it's persisted to localStorage independently of the
  // Firestore cache - skips this and keeps working, including offline.
  const isAwaitingFirstProfileSync =
    props.auth.data.uid !== null &&
    props.profile.data === null &&
    profileSyncState !== "ready";

  // The listener's own timeout only arms once auth resolves; this one covers
  // the gate even when it never does (token fetch failing, auth never firing).
  const [isGateTimedOut, setIsGateTimedOut] = useState(false);
  useEffect(() => {
    if (!isAwaitingFirstProfileSync) {
      setIsGateTimedOut(false);
      return;
    }
    const timer = setTimeout(
      () => setIsGateTimedOut(true),
      PROFILE_SYNC_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [isAwaitingFirstProfileSync]);

  if (isAwaitingFirstProfileSync) {
    return (
      <ProfileSyncGate
        unreachable={profileSyncState === "unreachable" || isGateTimedOut}
        onRetry={() => window.location.reload()}
        onLogout={() => logout()}
      />
    );
  }

  return (
    // process.env.REACT_APP_STAGING === "production" &&
    // window.matchMedia("(display-mode: standalone)").matches) ||
    // process.env.REACT_APP_STAGING !== "production" ? (
    <>
      <div className="font-color-tertiaryColor6 background-color-primary inherit-parent-height inherit-parent-width flex-justify-content-center flex-align-items-center overflow-hidden">
        <div
          style={{
            maxWidth: "767px"
          }}
          className="inherit-parent-height inherit-parent-width flex-justify-content-center flex-align-items-center"
        >
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  <Login navigate={navigate} />
                </PublicRoute>
              }
            />
            <Route
              path="/:locationId/procedures"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  {locationAccess !== "granted" && !window.Cypress ? (
                    <PermissionDeniedFallback
                      auth={props.auth}
                      fallBackImage={<LocationFallBack />}
                      navigate={navigate}
                      title={
                        locationAccess === "prompt"
                          ? "Allow Location Permission"
                          : "Location Permission Denied"
                      }
                      description={
                        locationAccess === "prompt"
                          ? "Enables us to monitor your real-time location"
                          : "You have blocked from tracking your location. To use this, change your location settings in browser."
                      }
                      showButton={locationAccess === "prompt"}
                      onEnableClick={() => {
                        requestLocationPermission();
                      }}
                    />
                  ) : (
                    <Procedures navigate={navigate} claims={auth.claims} />
                  )}
                </ProtectedRoute>
              }
            />

            <Route
              path="/faceIdentification/locationSearch"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  <LocationSearch
                    navigate={navigate}
                    auth={props.auth}
                    locations={
                      isValidObject(props.locations.data)
                        ? Object.values(props.locations.data)
                        : []
                    }
                    loading={props.locations.loading}
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/complaints/locationSearch"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  <LocationSearch
                    auth={props.auth}
                    navigate={navigate}
                    locations={
                      isValidObject(props.locations.data)
                        ? Object.values(props.locations.data)
                        : []
                    }
                    loading={props.locations.loading}
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/locationSearch"
              element={
                <ProtectedRoute
                  auth={props.auth}
                  profileData={props.profile.data}
                >
                  <LocationSearch
                    auth={props.auth}
                    navigate={navigate}
                    locations={
                      isValidObject(props.locations.data)
                        ? Object.values(props.locations.data)?.filter(
                            (data) => data.access === "public"
                          )
                        : []
                    }
                    loading={props.locations.loading}
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/procedures/:procedureId/*"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  {locationAccess !== "granted" && !window.Cypress ? (
                    <PermissionDeniedFallback
                      auth={props.auth}
                      fallBackImage={<LocationFallBack />}
                      navigate={navigate}
                      title={
                        locationAccess === "prompt"
                          ? "Allow Location Permission"
                          : "Location Permission Denied"
                      }
                      description={
                        locationAccess === "prompt"
                          ? "Enables us to monitor your real-time location"
                          : "You have blocked from tracking your location. To use this, change your location settings in browser."
                      }
                      showButton={locationAccess === "prompt"}
                      onEnableClick={() => {
                        requestLocationPermission();
                      }}
                    />
                  ) : (
                    <SelectedProcedure
                      navigate={navigate}
                      isOnline={isOnline}
                    />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  <Home
                    navigate={navigate}
                    claims={auth.claims}
                    auth={props.auth}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/complaints"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  <Complaints
                    navigate={navigate}
                    getNextSetOfComplaints={() => {
                      getComplaintsNextPage();
                    }}
                    paginationLoading={complaintsLoading.pagination}
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/complaints/:complaintId"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  <SelectedComplaint navigate={navigate} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/complaints/create"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  <CreateComplaint navigate={navigate} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/viewUpdates/:complaintId"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  <ViewUpdates navigate={navigate} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  <Profile navigate={navigate} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/faceIdentification/*"
              element={
                <ProtectedRoute
                  uid={props.auth.data.uid}
                  profileData={props.profile.data}
                >
                  {locationAccess !== "granted" && !window.Cypress ? (
                    <PermissionDeniedFallback
                      auth={props.auth}
                      navigate={navigate}
                      fallBackImage={<LocationFallBack />}
                      title={
                        locationAccess === "prompt"
                          ? "Allow Location Permission"
                          : "Location Permission Denied"
                      }
                      description={
                        locationAccess === "prompt"
                          ? "Turn on location tracking to access tasks"
                          : "You have blocked from tracking your location. To use this, change your location settings in browser."
                      }
                      showButton={locationAccess === "prompt"}
                      onEnableClick={() => {
                        requestLocationPermission();
                      }}
                    />
                  ) : cameraAccess !== "granted" && !window.Cypress ? (
                    <PermissionDeniedFallback
                      auth={props.auth}
                      navigate={navigate}
                      fallBackImage={<CameraFallBack />}
                      title={
                        cameraAccess === "prompt"
                          ? "Allow Camera Permission"
                          : "Camera Permission Denied"
                      }
                      description={
                        cameraAccess === "prompt"
                          ? "Camera permission required, Allow access to continue"
                          : "You have blocked from camera permission. To use this, change your camera settings in browser."
                      }
                      showButton={cameraAccess === "prompt"}
                      onEnableClick={() => {
                        requestCameraPermission();
                      }}
                    />
                  ) : (
                    <FaceIdentification
                      navigate={navigate}
                      isOnline={isOnline}
                    />
                  )}
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <SnackBar
            message={status}
            status={props.status}
            type={props.status.code === null ? "success" : "error"}
          />
        </div>
      </div>

      <div
        id="critical-update-card"
        className="display-none z-index-101 inherit-parent-height inherit-parent-width background-color-black-with-opacity-light position-absolute-center-self "
      >
        <footer className="position-absolute  inherit-parent-width bottom-0 padding-large">
          <div
            id="critical-update-card-contents-wrapper"
            style={{
              backgroundColor: "var(--tertiaryColor1)"
            }}
            className="font-color-tertiaryColor inherit-parent-width padding-large border-radius-default display-flex flex-direction-column flex-align-items-center "
          >
            <div className="margin-bottom-default">
              <UpdateReloadIcon color="var(--secondaryColor)" />
            </div>
            <p className="font-size-medium font-color-tertiaryColor6 text-align-center margin-bottom-default ">
              {" "}
              Critical update available{" "}
            </p>
            <p className="font-size-medium font-color-tertiaryColor5 text-align-center margin-bottom-default ">
              New critical changes updated and ready to launch.
            </p>
            <button
              style={{
                backgroundColor: "var(--secondaryColor)"
              }}
              id="critical-update-confirm-button"
              className="inherit-parent-width padding-vertical-medium border-radius-default font-size-medium cursor-pointer font-color-black box-shadow-none"
            >
              Okay
            </button>
          </div>
        </footer>
      </div>
    </>
  );
  // : (
  //   <UnsupportedDevice appName="doctors" />
  // );
}

const mapStateToProps = function (state) {
  return {
    auth: state.auth,
    complaints: state.complaints,
    locations: state.locations,
    procedures: state.procedures,
    profile: state.profile,
    status: state.status
  };
};

const mapDispatchToProps = function () {
  return {
    setSelectedProcedure: (data) => setSelectedProcedure(data),
    forceRefreshAccessToken: (status) => forceRefreshAccessToken(status)
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(App);

const ProtectedRoute = ({ uid, children, profileData }) => {
  if (uid === null && profileData === null) {
    return <Navigate to="/login" />;
  } else {
    return children;
  }
};

const PublicRoute = ({ uid, children, profileData }) => {
  if (uid === null && profileData === null) {
    return children;
  } else {
    return <Navigate to="/" />;
  }
};

// Shown only while an authenticated user's profile hasn't loaded yet - not
// on ordinary logout/login, which ProtectedRoute/PublicRoute already cover.
const ProfileSyncGate = ({ unreachable, onRetry, onLogout }) => {
  return (
    <div className="font-color-tertiaryColor6 background-color-primary inherit-parent-height inherit-parent-width display-flex flex-direction-column flex-justify-content-center flex-align-items-center padding-large overflow-hidden">
      <RotateIcon width="48" height="48" color="var(--tertiaryColor4)" removeDot />
      <div className="padding-top-larger font-color-tertiaryColor6 text-align-center">
        {unreachable ? "Could not load your profile" : "Connecting…"}
      </div>
      <div className="padding-top-medium text-align-center font-size-medium font-color-tertiaryColor5">
        {unreachable
          ? "Check your connection and try again."
          : "Setting up your account. This should only take a moment."}
      </div>
      {unreachable && (
        <div className="inherit-parent-width padding-top-larger">
          <Button
            boxShadow={false}
            type="button"
            text="Retry"
            onClick={onRetry}
          />
          <Button
            className="margin-top-default"
            boxShadow={false}
            type="button"
            text="Log out and sign in again"
            variant="transparent"
            onClick={onLogout}
          />
        </div>
      )}
    </div>
  );
};

const PermissionDeniedFallback = (props) => {
  // for iphone only
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    props.onEnableClick();
  }
  return (
    <div className="inherit-parent-width inherit-parent-height">
      <Header
        title={
          window.location.pathname.split("/")?.[
            window.location.pathname.split("/").length - 1
          ]
        }
        theme={props.auth.theme.primaryColor === "#0E0E0E" ? "dark" : "light"}
        backOnClick={() => {
          props.navigate(-1);
        }}
        profileOnClick={() => {
          props.navigate("/settings");
        }}
      />

      <div
        className="remaining-body-height padding-large display-flex flex-direction-column flex-justify-content-center flex-align-items-center"
        data-cy="location-permission-denied-fallback"
      >
        <div className="inherit-parent-height padding-horizontal-large display-flex flex-direction-column flex-justify-content-center flex-align-items-center">
          <div>{props.fallBackImage}</div>
          <div className="padding-top-larger font-color-tertiaryColor6">
            {props.title}
          </div>
          <div className=" padding-top-medium text-align-center font-size-medium font-color-tertiaryColor5">
            {props.description}
          </div>
        </div>
        {props.showButton && (
          <div className="inherit-parent-width">
            <Button
              boxShadow={false}
              type="button"
              text="Enable"
              disabled={/iPhone|iPad|iPod/i.test(navigator.userAgent)}
              onClick={props.onEnableClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const LocationFallBack = () => {
  return (
    <svg
      width="60"
      height="60"
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M33.7504 52.5L33.1904 51.38L25.0004 35L7.50042 26.25C7.26107 26.1403 7.05824 25.9642 6.91604 25.7426C6.77383 25.521 6.69824 25.2633 6.69824 25C6.69824 24.7367 6.77383 24.479 6.91604 24.2574C7.05824 24.0358 7.26107 23.8597 7.50042 23.75L52.5004 7.5L43.4454 32.575"
        stroke="#D17541"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M55 55L42.5 42.5"
        stroke="#D17541"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M42.5 55L55 42.5"
        stroke="#D17541"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const CameraFallBack = () => {
  return (
    <svg
      width="60"
      height="60"
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M33.75 50H12.5C11.1739 50 9.90215 49.4732 8.96447 48.5355C8.02678 47.5979 7.5 46.3261 7.5 45V22.5C7.5 21.1739 8.02678 19.9021 8.96447 18.9645C9.90215 18.0268 11.1739 17.5 12.5 17.5H15C16.3261 17.5 17.5979 16.9732 18.5355 16.0355C19.4732 15.0979 20 13.8261 20 12.5C20 11.837 20.2634 11.2011 20.7322 10.7322C21.2011 10.2634 21.837 10 22.5 10H37.5C38.163 10 38.7989 10.2634 39.2678 10.7322C39.7366 11.2011 40 11.837 40 12.5C40 13.8261 40.5268 15.0979 41.4645 16.0355C42.4021 16.9732 43.6739 17.5 45 17.5H47.5C48.8261 17.5 50.0979 18.0268 51.0355 18.9645C51.9732 19.9021 52.5 21.1739 52.5 22.5V32.5"
        stroke="#D17541"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22.5 32.5C22.5 34.4891 23.2902 36.3968 24.6967 37.8033C26.1032 39.2098 28.0109 40 30 40C31.9891 40 33.8968 39.2098 35.3033 37.8033C36.7098 36.3968 37.5 34.4891 37.5 32.5C37.5 30.5109 36.7098 28.6032 35.3033 27.1967C33.8968 25.7902 31.9891 25 30 25C28.0109 25 26.1032 25.7902 24.6967 27.1967C23.2902 28.6032 22.5 30.5109 22.5 32.5Z"
        stroke="#D17541"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M55 55L42.5 42.5"
        stroke="#D17541"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M42.5 55L55 42.5"
        stroke="#D17541"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
